import {
  useLegalAgreements,
  useLegalAgreementAcceptanceMutations,
  useUnsignedAchAccounts
} from '@/app/legal-agreement/use-legal-agreement'
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Modal } from '@klear/ui/components/modal'
import { Button } from '@klear/ui/components/button'
import { Checkbox } from '@klear/ui/components/checkbox'
import { useAuth } from '@klear/ui/contexts/auth-context'
import SignatureCanvas from 'react-signature-canvas'
import { Label } from '@klear/ui/components/label'
import { FileText, Shield, AlertTriangle } from 'lucide-react'
import type { LegalAgreement } from '@klear/legal-agreement'
import { CHECKBOX_CONSENT_PREFIX, E_SIGNATURE_CONSENT_TEXT } from '@klear/constants'
import { GeneratedLegalAgreementViewer } from '@/app/legal-agreement/generated-legal-agreement-viewer'
import { AchAgreementContent } from '@/app/ach-agreement-content'

type LegalAgreementModalMode = 'user_agreements' | 'ach_agreement'

interface LegalAgreementCardProps {
  agreement: LegalAgreement
  onView: () => void
}

const LegalAgreementCard = ({ agreement, onView }: LegalAgreementCardProps) => {
  const Icon = agreement.name === 'privacy_policy' ? Shield : FileText
  const description =
    agreement.name === 'privacy_policy'
      ? 'Learn how we collect, use, and protect your personal information.'
      : 'Review the terms and conditions for using our services.'

  return (
    <div className="bg-muted/50 rounded-lg p-4 border border-border">
      <div className="flex items-start space-x-3">
        <div className="shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm text-foreground mb-1">{agreement.title}</h3>
          <p className="text-xs text-muted-foreground mb-3">{description}</p>
          <button onClick={onView} className="text-xs underline flex items-center gap-1">
            <FileText className="w-3 h-3" />
            Read{agreement.signatureRequired ? ' and sign' : ''} {agreement.title}
          </button>
        </div>
      </div>
    </div>
  )
}

interface LegalAgreementViewerModalProps {
  agreement: LegalAgreement
  traderId: number
  open: boolean
  onClose: () => void
  signatureRequired?: boolean
  signatureRef?: React.RefObject<SignatureCanvas>
  hasSignature?: boolean
  onSignatureEnd?: () => void
  onClearSignature?: () => void
  onAccept?: () => void
  isSubmitting?: boolean
  hideOverlay?: boolean
}

const LegalAgreementViewerModal = ({
  agreement,
  traderId,
  open,
  onClose,
  signatureRequired = false,
  signatureRef,
  hasSignature = false,
  onSignatureEnd,
  onClearSignature,
  onAccept,
  isSubmitting = false,
  hideOverlay = false
}: LegalAgreementViewerModalProps) => {
  if (!open) return null

  const fileUrl = agreement.getFileUrl(traderId)
  const fileExtension = agreement.url.split('.').pop()?.toLowerCase() || ''
  const isPdf = fileExtension === 'pdf'

  return (
    <Modal
      title={agreement.title}
      open={open}
      disableOutsideClick={true}
      hideOverlay={hideOverlay}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose()
      }}
    >
      <div className={signatureRequired ? 'space-y-4' : 'flex flex-col flex-1 min-h-0 h-full'}>
        <div
          className={`border rounded-lg bg-card shadow-sm w-full overflow-auto ${
            signatureRequired ? 'max-h-[50vh] h-[50vh]' : 'flex-1 min-h-0'
          }`}
        >
          {isPdf ? (
            <embed src={fileUrl} type="application/pdf" className="w-full h-full" title={agreement.title} />
          ) : (
            <iframe src={fileUrl} className="w-full h-full border-0" title={agreement.title} />
          )}
        </div>

        {signatureRequired && (
          <div className="space-y-2">
            <Label>Signature</Label>
            <div className="border rounded-lg">
              <SignatureCanvas
                ref={signatureRef}
                onEnd={onSignatureEnd}
                penColor="#000000"
                canvasProps={{
                  className: 'w-full h-48 rounded-lg bg-white',
                  style: { touchAction: 'none' }
                }}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onClearSignature} className="flex-1">
                Clear Signature
              </Button>
              <Button size="sm" onClick={onAccept} disabled={!hasSignature || isSubmitting} className="flex-1">
                {isSubmitting ? 'Submitting...' : 'Accept Signature'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

type ShowOnceDecision = boolean | null

const LegalAgreements = () => {
  const { user, logout } = useAuth()
  const traderId = user?.traderIds[0] || 0
  const { missingUserLegalAgreements, isLoading: isLoadingUserAgreements } = useLegalAgreements()
  const { createUserLegalAgreementAcceptance, isCreatingUserLegalAgreementAcceptance } =
    useLegalAgreementAcceptanceMutations()
  const {
    achAgreement,
    unsignedExternalAccounts,
    unsignedKlearAccounts,
    hasUnsignedAccounts,
    isLoading: isLoadingAch
  } = useUnsignedAchAccounts()

  const [achViewerOpen, setAchViewerOpen] = useState(false)
  const [selectedAgreementId, setSelectedAgreementId] = useState<number | null>(null)
  const [hasSignature, setHasSignature] = useState(false)
  const signatureRef = useRef<SignatureCanvas>(null)
  const [acceptedAgreementIds, setAcceptedAgreementIds] = useState<Set<number>>(new Set())
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [checkedAgreements, setCheckedAgreements] = useState<Set<number>>(new Set())
  const previousAccountCountRef = useRef<number | null>(null)
  const [showOnceDecision, setShowOnceDecision] = useState<ShowOnceDecision>(null)

  const agreementModels = useMemo(() => missingUserLegalAgreements ?? [], [missingUserLegalAgreements])
  const hasPendingUserAgreements = agreementModels.length > 0
  const accountCount = unsignedExternalAccounts.length + unsignedKlearAccounts.length
  const showAchFlow = hasUnsignedAccounts && !!achAgreement && !hasPendingUserAgreements

  const dataSettled = !isLoadingUserAgreements && (!user || user.hasKlearRole ? true : !isLoadingAch)

  const modalMode: LegalAgreementModalMode = useMemo(() => {
    if (hasPendingUserAgreements) return 'user_agreements'
    if (showAchFlow) return 'ach_agreement'
    return 'user_agreements'
  }, [hasPendingUserAgreements, showAchFlow])

  const { checkboxAgreements, signatureAgreements, dynamicAgreements } = useMemo(() => {
    const checkbox: LegalAgreement[] = []
    const signature: LegalAgreement[] = []
    const dynamic: LegalAgreement[] = []

    agreementModels.forEach((agreement) => {
      if (agreement.isDynamic) {
        dynamic.push(agreement)
      } else if (agreement.signatureRequired) {
        signature.push(agreement)
      } else {
        checkbox.push(agreement)
      }
    })

    return { checkboxAgreements: checkbox, signatureAgreements: signature, dynamicAgreements: dynamic }
  }, [agreementModels])

  const lastUpdatedDate = useMemo(() => {
    const dates = agreementModels.map((a) => a.createdAt).filter((d): d is NonNullable<typeof d> => d != null)
    if (dates.length === 0) return null
    return dates.reduce((latest, current) => (current.isAfter(latest) ? current : latest))
  }, [agreementModels])

  const selectedAgreement = useMemo(() => {
    if (selectedAgreementId === null) return null
    return agreementModels.find((a) => a.id === selectedAgreementId) ?? null
  }, [selectedAgreementId, agreementModels])

  const allCheckboxAgreementsChecked = useMemo(() => {
    return checkboxAgreements.length > 0 && checkboxAgreements.every((a) => checkedAgreements.has(a.id))
  }, [checkboxAgreements, checkedAgreements])

  const allAgreementsAccepted = useMemo(() => {
    return agreementModels.length > 0 && agreementModels.every((a) => acceptedAgreementIds.has(a.id))
  }, [agreementModels, acceptedAgreementIds])

  const isOpen =
    showOnceDecision === true && (modalMode === 'user_agreements' ? !allAgreementsAccepted : accountCount > 0)

  useEffect(() => {
    if (showOnceDecision === null) {
      if (dataSettled) {
        setShowOnceDecision(hasPendingUserAgreements || showAchFlow)
      }
      return
    }

    const prev = previousAccountCountRef.current
    previousAccountCountRef.current = accountCount

    const userFlowDone = modalMode === 'user_agreements' && allAgreementsAccepted
    const achFlowEmpty = modalMode === 'ach_agreement' && accountCount === 0
    const achSignedElsewhere = modalMode === 'ach_agreement' && prev != null && accountCount < prev && !achViewerOpen

    if (userFlowDone || achFlowEmpty || achSignedElsewhere) {
      if (achFlowEmpty || achSignedElsewhere) setAchViewerOpen(false)
      setShowOnceDecision(false)
    }
  }, [
    showOnceDecision,
    dataSettled,
    hasPendingUserAgreements,
    showAchFlow,
    modalMode,
    allAgreementsAccepted,
    accountCount,
    achViewerOpen
  ])

  const handleCheckboxChange = (agreementId: number, checked: boolean) => {
    setCheckedAgreements((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(agreementId)
      } else {
        next.delete(agreementId)
      }
      return next
    })
  }

  const handleAcceptAndContinue = async () => {
    if (!allCheckboxAgreementsChecked || checkboxAgreements.length === 0) return

    try {
      const agreementsToAccept = checkboxAgreements.filter((agreement) => checkedAgreements.has(agreement.id))
      await Promise.all(
        agreementsToAccept.map((agreement) =>
          createUserLegalAgreementAcceptance(agreement.id, undefined, CHECKBOX_CONSENT_PREFIX + agreement.title)
        )
      )

      const acceptedIds = new Set(agreementsToAccept.map((a) => a.id))
      setAcceptedAgreementIds((prev) => new Set([...prev, ...acceptedIds]))
      setCheckedAgreements(new Set())
    } catch (error) {
      console.error('Failed to accept legal agreements:', error)
    }
  }

  const handleDocumentModalClose = () => {
    setSelectedAgreementId(null)
    setHasSignature(false)
    signatureRef.current?.clear()
  }

  const handleSignatureAcceptance = async () => {
    if (!selectedAgreement?.signatureRequired || !signatureRef.current || signatureRef.current.isEmpty()) {
      return
    }

    const canvas = signatureRef.current.getCanvas()
    const signature = canvas.toDataURL()

    const consentText = selectedAgreement.esignConsentText ?? E_SIGNATURE_CONSENT_TEXT
    try {
      await createUserLegalAgreementAcceptance(selectedAgreement.id, signature, consentText)
      const newAcceptedIds = new Set(acceptedAgreementIds).add(selectedAgreement.id)
      setAcceptedAgreementIds(newAcceptedIds)
      handleDocumentModalClose()
    } catch (error) {
      console.error('Failed to accept legal agreement:', error)
    }
  }

  const handleSignatureEnd = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setHasSignature(true)
    }
  }

  const handleClearSignature = () => {
    signatureRef.current?.clear()
    setHasSignature(false)
  }

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true)
      await logout()
    } catch (error) {
      console.error('Logout error:', error)
      setIsLoggingOut(false)
    }
  }

  const handleAchSignSuccess = useCallback(() => {
    setAchViewerOpen(false)
    setShowOnceDecision(false)
  }, [])

  const generatedViewerAgreement = useMemo(() => {
    if (achAgreement && modalMode === 'ach_agreement' && achViewerOpen) return achAgreement
    if (selectedAgreement?.isDynamic && selectedAgreementId !== null) return selectedAgreement
    return null
  }, [achAgreement, modalMode, achViewerOpen, selectedAgreement, selectedAgreementId])

  const handleGeneratedViewerClose = useCallback(() => {
    if (generatedViewerAgreement === achAgreement) {
      setAchViewerOpen(false)
    } else {
      handleDocumentModalClose()
    }
  }, [generatedViewerAgreement, achAgreement])

  const handleGeneratedViewerSuccess = useCallback(() => {
    if (generatedViewerAgreement === achAgreement) {
      handleAchSignSuccess()
    } else if (generatedViewerAgreement) {
      const newAcceptedIds = new Set(acceptedAgreementIds).add(generatedViewerAgreement.id)
      setAcceptedAgreementIds(newAcceptedIds)
      handleDocumentModalClose()
    }
  }, [generatedViewerAgreement, achAgreement, acceptedAgreementIds, handleAchSignSuccess])

  if (showOnceDecision !== true) {
    return null
  }

  return (
    <>
      <Modal
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) setShowOnceDecision(false)
        }}
        hideCloseButton={modalMode !== 'ach_agreement'}
        title=""
        description=""
        disableOutsideClick={modalMode !== 'ach_agreement'}
        disableEscapeKey={modalMode !== 'ach_agreement'}
      >
        <div className="flex flex-col -mx-4 -mt-4 -mb-8">
          {modalMode === 'ach_agreement' ? (
            <AchAgreementContent
              userEmail={user?.email}
              accountCount={accountCount}
              externalCount={unsignedExternalAccounts.length}
              klearCount={unsignedKlearAccounts.length}
              onReviewAndSign={() => setAchViewerOpen(true)}
            />
          ) : (
            <>
              <div className="bg-yellow-50 border-b border-yellow-200 dark:bg-yellow-950 dark:border-yellow-900 rounded-t-lg px-4 py-3">
                <div className="flex items-start gap-2 pt-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-foreground mb-1">Action Required</div>
                    <div className="text-xs text-foreground">
                      Our{' '}
                      {agreementModels
                        .map((a) => a.title)
                        .filter((title) => title)
                        .join(' and ')}{' '}
                      {agreementModels.length > 1 ? 'have' : 'has'} been updated. Please review and accept to continue.
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-4 pt-6 pb-4">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-foreground mb-2">Updated Terms & Policies</h2>
                  {user && <div className="text-sm text-muted-foreground mb-2">Welcome back, {user.email}</div>}
                  {lastUpdatedDate && (
                    <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground">
                      Last updated: {lastUpdatedDate.toString('MMMM D, YYYY')}
                    </div>
                  )}
                </div>

                <div className="space-y-4 mb-6">
                  {agreementModels.map((agreement) => (
                    <LegalAgreementCard
                      key={agreement.id}
                      agreement={agreement}
                      onView={() => {
                        setSelectedAgreementId(agreement.id)
                        signatureRef.current?.clear()
                        setHasSignature(false)
                      }}
                    />
                  ))}
                </div>

                {checkboxAgreements.length > 0 && (
                  <div className="space-y-3 mb-6">
                    {checkboxAgreements.map((agreement) => (
                      <div key={agreement.id} className="flex items-start space-x-2">
                        <Checkbox
                          id={`agreement-${agreement.id}`}
                          checked={checkedAgreements.has(agreement.id)}
                          onCheckedChange={(checked) => handleCheckboxChange(agreement.id, checked === true)}
                          disabled={acceptedAgreementIds.has(agreement.id)}
                        />
                        <Label
                          htmlFor={`agreement-${agreement.id}`}
                          className="cursor-pointer text-sm leading-5"
                          onClick={() => {
                            if (!acceptedAgreementIds.has(agreement.id)) {
                              handleCheckboxChange(agreement.id, !checkedAgreements.has(agreement.id))
                            }
                          }}
                        >
                          I have read and agree to the updated {agreement.title}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}

                {checkboxAgreements.length > 0 && (
                  <Button
                    onClick={handleAcceptAndContinue}
                    disabled={!allCheckboxAgreementsChecked || isCreatingUserLegalAgreementAcceptance}
                    className="w-full mb-4"
                  >
                    {isCreatingUserLegalAgreementAcceptance ? 'Submitting...' : 'Accept and continue'}
                  </Button>
                )}

                {checkboxAgreements.length === 0 &&
                  (signatureAgreements.length > 0 || dynamicAgreements.length > 0) && (
                    <div className="text-sm text-muted-foreground mb-4 text-center">
                      Please review {dynamicAgreements.length > 0 ? 'and accept' : 'and sign'} the agreements above to
                      continue.
                    </div>
                  )}

                <div className="text-center">
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut || isCreatingUserLegalAgreementAcceptance}
                    className="text-sm text-muted-foreground hover:text-foreground underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoggingOut ? 'Logging out...' : "I don't agree (sign out)"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>

      {generatedViewerAgreement && (
        <GeneratedLegalAgreementViewer
          agreement={generatedViewerAgreement}
          traderId={traderId}
          open={true}
          onClose={handleGeneratedViewerClose}
          onSuccess={handleGeneratedViewerSuccess}
          externalAccountIds={unsignedExternalAccounts.map((a: { id: number }) => a.id)}
          klearAccountIds={unsignedKlearAccounts.map((a: { id: number }) => a.id)}
          hideOverlay
        />
      )}

      {selectedAgreement && !selectedAgreement.isDynamic && (
        <LegalAgreementViewerModal
          agreement={selectedAgreement}
          traderId={traderId}
          open={selectedAgreementId !== null}
          onClose={handleDocumentModalClose}
          signatureRequired={selectedAgreement.signatureRequired}
          signatureRef={signatureRef}
          hasSignature={hasSignature}
          onSignatureEnd={handleSignatureEnd}
          onClearSignature={handleClearSignature}
          onAccept={handleSignatureAcceptance}
          isSubmitting={isCreatingUserLegalAgreementAcceptance}
          hideOverlay
        />
      )}
    </>
  )
}

export default LegalAgreements
